/**
 * Firebase Configuration for GOATnote EHR
 * Healthcare EHR Development Project
 * Prevents duplicate initialization errors
 */

// Firebase Configuration for Development and Production
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';

// Determine if we're in development
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Use environment variables for Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyCdlcxXNqlQt9PRDOhChF2RmBRnge7z2-0",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "goatnote-ehr-dev-3799-d7759.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "goatnote-ehr-dev-3799-d7759",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "goatnote-ehr-dev-3799-d7759.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "818143693907",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:818143693907:web:dd3b759371e52173f0caa6",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-SHJFLQMP9B"
};

console.log('🔥 Firebase Config:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  environment: process.env.NODE_ENV,
  isDevelopment
});

// Initialize Firebase only if not already initialized
let app;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
  console.log('🔥 Firebase initialized successfully for project:', firebaseConfig.projectId);
} else {
  app = getApp();
  console.log('🔥 Firebase app already exists, using existing instance');
}

// Initialize Auth and Firestore
export const auth = getAuth(app);
export const db = getFirestore(app);

// Use production Firebase (no emulators for this setup)
// Emulators disabled to prevent network-request-failed errors
console.log('🔥 Firebase connected to production Firebase services');

// Optional: Enable emulators only if explicitly configured
if (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
  try {
    connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    console.log('🔌 Firebase emulators enabled via environment variable');
  } catch (error) {
    console.warn('Firebase emulator connection failed:', error.message);
  }
}

export default app;

// Action code settings for passwordless authentication
export const actionCodeSettings = {
  // URL to redirect back to. The domain must be whitelisted in Firebase Console
  url: process.env.REACT_APP_CONFIRM_EMAIL_URL || 'http://localhost:3000/confirm',
  // This must be true for passwordless sign-in
  handleCodeInApp: true,
  // Optional: iOS and Android app configuration
  // iOS: {
  //   bundleId: 'com.goatnote.ehr.ios'
  // },
  // android: {
  //   packageName: 'com.goatnote.ehr.android',
  //   installApp: true,
  //   minimumVersion: '12'
  // }
};

// Note: You'll need to get these values from your Firebase Console:
// 1. Go to Project Settings
// 2. Scroll down to "Your apps" section
// 3. Find your web app
// 4. Copy the configuration values
// 5. Add them to your .env.local file

// Environment-specific configurations
export const firestoreSettings = {
  // Enable offline persistence for reliability
  cacheSizeBytes: 50 * 1024 * 1024, // 50MB cache
  experimentalForceLongPolling: false,
  experimentalAutoDetectLongPolling: true,
};

// Collection names
export const COLLECTIONS = {
  BEDS: 'beds',
  ZONES: 'zones',
  ALERTS: 'alerts',
  PRESENCE: 'presence',
  AUDIT: 'audit'
};

// Bed statuses
export const BED_STATUS = {
  AVAILABLE: 'available',
  OCCUPIED: 'occupied',
  CLEANING: 'cleaning',
  MAINTENANCE: 'maintenance',
  RESERVED: 'reserved'
};

// Alert severities
export const ALERT_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// User roles for security rules
export const USER_ROLES = {
  ADMIN: 'admin',
  DOCTOR: 'doctor',
  NURSE: 'nurse',
  CLINICIAN: 'clinician',
  VIEWER: 'viewer'
}; 
// OAuth Configuration Notes:
// IAP Client ID: 217612730390-j0o6pl7m0amme1k4fbnfubt59me5pifs.apps.googleusercontent.com (for backend authentication)
// Web App Client ID: [CREATE MANUALLY - see oauth-manual-setup-guide.txt]
